import React, { createContext, useState, useEffect, useCallback } from 'react';
import {
  API_BASE_URL,
  loginUserApi,
  registerUserApi,
  uploadImageApi,
  createProductApi,
  fetchProductsApi,
  syncUserDataApi,
  createOrderApi,
  fetchOrdersApi,
  updateOrderStatusApi,
  updateProductApi,
  deleteProductApi,
  deleteOrderApi,
  fetchVideosApi,
  createVideoApi,
  updateVideoApi,
  deleteVideoApi,
  fetchBlogsApi,
  createBlogApi,
  updateBlogApi,
  deleteBlogApi,
  fetchContributorsApi,
  createContributorApi,
  updateContributorApi,
  deleteContributorApi
} from '../services/api';
import { sanitizeProductsForStorage } from './storageUtils';
import { getSizeExtraAmount } from '../utils/pricing';

export const ShopContext = createContext();

const normalizeUploadPath = (pathValue) => {
  if (!pathValue || typeof pathValue !== 'string') return '';
  const clean = pathValue.replace(/\\/g, '/').trim();
  if (clean.startsWith('http') || clean.startsWith('blob:') || clean.startsWith('data:') || clean.startsWith('/')) return clean;
  const filename = clean.split('/').pop();
  return filename ? `/uploads/${filename}` : '';
};

export const getCategoryFallbackImage = (category) => {
  const cat = (category || '').toLowerCase();
  if (cat.includes('hoodie')) return '/images/cat-slide-men-hoodie.webp';
  if (cat.includes('women') && cat.includes('polo')) return '/images/cat-slide-women-polo.webp';
  if (cat.includes('women')) return '/images/cat-slide-women-classic.webp';
  if (cat.includes('jersey') || cat.includes('sport')) return '/images/cat-slide-sports-jersey.webp';
  if (cat.includes('oversized')) return '/images/cat-slide-women-hoodies.webp';
  if (cat.includes('shirt')) return '/images/cat-slide-men-classic.webp';
  return '/images/cat-slide-men-polo.webp';
};

export const getProductImage = (imagePath, fallbackCat = '') => {
  const defaultFallback = getCategoryFallbackImage(fallbackCat);
  if (!imagePath) return defaultFallback;
  if (typeof imagePath === 'object') {
    imagePath = imagePath.preview || imagePath.url || imagePath.filePath || imagePath.imageUrl || '';
  }
  if (typeof imagePath === 'string' && imagePath.trim().startsWith('{')) {
    try {
      const parsed = JSON.parse(imagePath);
      imagePath = parsed.preview || parsed.url || parsed.filePath || imagePath;
    } catch (e) {}
  }
  if (!imagePath || typeof imagePath !== 'string') return defaultFallback;
  let cleanPath = imagePath.replace(/\\/g, '/').trim();

  // Known broken/missing legacy image paths fallback to proper category image
  if (cleanPath.includes('unfold-polo') || cleanPath.includes('missing') || cleanPath === '/images/' || cleanPath === '/uploads/') {
    return defaultFallback;
  }

  // Automatically upgrade /images/ PNG/JPG to high-speed WebP (reduces size from 1.2MB to 30KB)
  if (cleanPath.startsWith('/images/') && /\.(png|jpg|jpeg|JPG|PNG)$/i.test(cleanPath)) {
    cleanPath = cleanPath.replace(/\.(png|jpg|jpeg|JPG|PNG)$/i, '.webp');
  }

  if (
    cleanPath.startsWith('http://') ||
    cleanPath.startsWith('https://') ||
    cleanPath.startsWith('data:') ||
    cleanPath.startsWith('blob:') ||
    cleanPath.startsWith('/images/')
  ) {
    return cleanPath;
  }
  const leadingPath = cleanPath.startsWith('/') ? cleanPath : `/${cleanPath}`;
  return `${API_BASE_URL}${leadingPath}`;
};

export const resolveProductImage = (product) => {
  if (!product) return '/images/cat-slide-men-polo.webp';
  const fallback = getCategoryFallbackImage(product.category);

  let raw = product.image || product.imageUrl || product.image_url;
  if (raw && typeof raw === 'object') {
    raw = raw.preview || raw.url || raw.filePath || raw.imageUrl || '';
  }
  if (typeof raw === 'string' && raw.trim().startsWith('{')) {
    try {
      const parsed = JSON.parse(raw);
      raw = parsed.preview || parsed.url || parsed.filePath || raw;
    } catch (e) {}
  }

  if (typeof raw === 'string' && raw.trim().length > 0 && !raw.trim().startsWith('{')) {
    return getProductImage(raw, product.category);
  }

  if (Array.isArray(product.images) && product.images.length > 0) {
    for (const item of product.images) {
      let candidate = typeof item === 'object' ? (item.preview || item.url || item.filePath) : item;
      if (typeof candidate === 'string' && candidate.trim().length > 0) {
        return getProductImage(candidate, product.category);
      }
    }
  }

  return fallback;
};

export const resolveProductThumbnail = (product) => {
  const fullImg = resolveProductImage(product);
  if (!fullImg) return '/images/cat-slide-men-polo.webp';
  if (fullImg.includes('/uploads/')) {
    // Return thumbnail endpoint
    return fullImg.includes('?') ? `${fullImg}&thumb=1` : `${fullImg}?thumb=1`;
  }
  return fullImg;
};

const CATALOG_VERSION = 'pallywear-categories-v8';

export const CATEGORIES = ['All', 'Mens', 'Womens', 'Sports'];

export const POLO_TYPES = [
  'Blended Polo',
  'Everyday Polo',
  'Comfort Polo'
];

export const resolveProductPoloType = (product) => {
  if (!product) return 'Everyday Polo';
  
  // 1. Direct explicit poloType / subCategory property
  const explicit = (product.poloType || product.polo_type || product.subCategory || product.sub_category || '').toLowerCase().trim();
  if (explicit === 'blended polo' || explicit === 'blended polos' || explicit === 'blended' || explicit === 'silk blend') {
    return 'Blended Polo';
  }
  if (explicit === 'comfort polo' || explicit === 'comfort polos' || explicit === 'comfort' || explicit === 'cloud comfort') {
    return 'Comfort Polo';
  }
  if (explicit === 'everyday polo' || explicit === 'everyday polos' || explicit === 'everyday' || explicit === 'daily essential') {
    return 'Everyday Polo';
  }

  const name = (product.name || '').toLowerCase().trim();
  const tag = (product.tag || '').toLowerCase().trim();
  const cat = (product.category || '').toLowerCase().trim();

  // 2. Strict Name / Tag / Category matching (prioritized and mutually exclusive)
  if (
    name.includes('blended polo') || name.includes('blend polo') || name.includes('silk blend') || name.includes('modal') ||
    tag === 'silk blend' || tag === 'blend' || cat === 'blended polo' || cat === 'blended polos' || cat === 'blended'
  ) {
    return 'Blended Polo';
  }

  if (
    name.includes('comfort polo') || name.includes('comfort stretch') || name.includes('cloud-touch') || name.includes('cloud touch') ||
    tag === 'cloud comfort' || tag === 'comfort' || cat === 'comfort polo' || cat === 'comfort polos' || cat === 'comfort'
  ) {
    return 'Comfort Polo';
  }

  if (
    name.includes('everyday polo') || name.includes('every day polo') || name.includes('classic pique') || name.includes('tailored knit polo') ||
    tag === 'daily essential' || tag === 'everyday' || tag === 'essential' || cat === 'everyday polo' || cat === 'everyday polos' || cat === 'everyday'
  ) {
    return 'Everyday Polo';
  }

  // Secondary checks:
  if (name.includes('silk') || name.includes('blend')) {
    return 'Blended Polo';
  }
  if (name.includes('stretch') || name.includes('comfort') || name.includes('cloud')) {
    return 'Comfort Polo';
  }
  if (name.includes('pique') || name.includes('everyday') || name.includes('every day') || name.includes('daily')) {
    return 'Everyday Polo';
  }

  return 'Everyday Polo';
};

export const MEN_CATEGORIES = [
  'Polos',
  'Hoodies',
  'Shirts',
  'Oversized T-Shirts'
];

export const WOMEN_CATEGORIES = [
  'Polos',
  'Hoodies',
  'Shirts',
  'Oversized T-Shirts'
];

export const SPORTS_CATEGORIES = [
  'Sports Crew Neck',
  'Sports Polo'
];

export const SUB_CATEGORIES = [
  'Polos',
  'Blended Polo',
  'Everyday Polo',
  'Comfort Polo',
  'Hoodies',
  'Shirts',
  'Oversized T-Shirts',
  'Sports Crew Neck',
  'Sports Polo',
  'Sports Jerseys',
  'Sports T-Shirts'
];

export const parseShopFiltersFromLocation = () => {
  if (typeof window === 'undefined') return { gender: 'All', subCategory: 'All', category: 'All' };
  
  const rawHash = (window.location.hash || '').replace(/^#\/?/, '');
  const hashWithoutQuery = rawHash.split('?')[0].toLowerCase();
  const searchStr = window.location.search || (rawHash.includes('?') ? '?' + rawHash.split('?')[1] : '');
  const pathname = (window.location.pathname || '').replace(/^\//, '').toLowerCase();

  let gender = 'All';
  let subCategory = 'All';
  let category = 'All';

  // 1. Query parameters (e.g. ?gender=Mens&sub=Blended+Polo or ?cat=...)
  if (searchStr) {
    try {
      const params = new URLSearchParams(searchStr);
      if (params.has('gender')) {
        const g = params.get('gender');
        if (g) gender = g;
      }
      if (params.has('sub') || params.has('subcategory')) {
        const s = params.get('sub') || params.get('subcategory');
        if (s) subCategory = s;
      }
      if (params.has('cat') || params.has('category')) {
        const c = params.get('cat') || params.get('category');
        if (c) category = c;
      }
    } catch (e) {}
  }

  // 2. Hash-based path or Pathname:
  // e.g. #shop/blended-polo, #shop/belended-polo, #shop/everyday-polo, #shop/comfort-polo
  // #shop/hoodie, #shop/shirts, #shop/oversize-tshirt, #shop/sports-crew-neck, #shop/sports-polo
  // #shop/men, #shop/women, #shop/sports, #shop/men/blended-polo, etc.
  const pathToCheck = hashWithoutQuery.startsWith('shop')
    ? hashWithoutQuery
    : pathname.startsWith('shop')
      ? pathname
      : '';

  if (pathToCheck) {
    const parts = pathToCheck.split('/').filter(Boolean);
    if (parts.length > 1) {
      const subParts = parts.slice(1);
      subParts.forEach(part => {
        let safePart = part;
        try {
          safePart = decodeURIComponent(part);
        } catch (error) {
          safePart = part;
        }

        const p = safePart.toLowerCase().trim().replace(/_/g, '-').replace(/\+/g, ' ');

        // Gender matching
        if (p === 'men' || p === 'mens') {
          gender = 'Mens';
        } else if (p === 'women' || p === 'womens') {
          gender = 'Womens';
        } else if (p === 'sports' || p === 'sport' || p === 'sportswear' || p === 'unisex') {
          gender = 'Sports';
        }

        // Subcategory matching (including user typos like 'belended-polo' / 'belended polo')
        if (p.includes('belended') || p.includes('blended')) {
          subCategory = 'Blended Polo';
        } else if (p.includes('everyday')) {
          subCategory = 'Everyday Polo';
        } else if (p.includes('comfort')) {
          subCategory = 'Comfort Polo';
        } else if (p.includes('hoodie')) {
          subCategory = 'Hoodies';
        } else if (p.includes('shirt') && !p.includes('tshirt') && !p.includes('t-shirt') && !p.includes('tee')) {
          subCategory = 'Shirts';
        } else if (p.includes('oversize')) {
          subCategory = 'Oversized T-Shirts';
        } else if (p.includes('sports-polo') || (gender === 'Sports' && p.includes('polo'))) {
          gender = 'Sports';
          subCategory = 'Sports Polo';
        } else if (p.includes('sports-crew') || p.includes('sports-crow') || p.includes('crew-neck') || p.includes('crewneck') || p.includes('sports-jersey') || p.includes('sports-jerseys') || p === 'jersey' || p === 'jerseys' || p.includes('sports jersey') || p.includes('sports-tshirt') || p.includes('sports-t-shirt') || p.includes('sports-tee') || p.includes('sports-t-shirts') || p.includes('sports tshirt')) {
          gender = 'Sports';
          subCategory = 'Sports Crew Neck';
        } else if (p === 'polo' || p === 'polos' || p === 'all-polos') {
          subCategory = 'Polos';
        }
      });
    }
  }

  return { gender, subCategory, category };
};

export const getSlugFromFilters = (gender, subCategory) => {
  const g = (gender || 'All').toLowerCase().trim();
  const sub = (subCategory || 'All').toLowerCase().trim();

  let genderPart = '';
  if (g === 'mens' || g === 'men') genderPart = 'men';
  else if (g === 'womens' || g === 'women') genderPart = 'women';
  else if (g === 'sports') genderPart = 'sports';

  let subPart = '';
  if (sub === 'blended polo' || sub.includes('blended') || sub.includes('belended')) subPart = 'blended-polo';
  else if (sub === 'everyday polo' || sub.includes('everyday')) subPart = 'everyday-polo';
  else if (sub === 'comfort polo' || sub.includes('comfort')) subPart = 'comfort-polo';
  else if (sub === 'hoodies' || sub === 'hoodie') subPart = 'hoodie';
  else if (sub === 'shirts' || sub === 'shirt') subPart = 'shirts';
  else if (sub === 'oversized t-shirts' || sub.includes('oversize')) subPart = 'oversize-tshirt';
  else if (sub === 'sports crew neck' || sub === 'sports crewneck' || sub === 'sports crow neck' || sub.includes('crew') || sub === 'sports t-shirts' || sub === 'sports jerseys' || sub.includes('sports-jersey') || sub.includes('sports-tshirt')) subPart = 'sports-crew-neck';
  else if (sub === 'sports polo' || sub.includes('sports-polo') || (g === 'sports' && sub.includes('polo'))) subPart = 'sports-polo';
  else if (sub === 'polos' || sub === 'polo') subPart = 'polos';

  if (genderPart && subPart) {
    if (genderPart === 'sports') return `sports/${subPart}`;
    return `${genderPart}/${subPart}`;
  }
  if (subPart) return subPart;
  if (genderPart) return genderPart;
  return '';
};

const INITIAL_PRODUCTS = [
  {
    id: 'db-12',
    name: 'Air-Flow Activewear Sports Tee',
    price: 899,
    category: 'Sports T-Shirts',
    gender: 'Sports',
    stock: 30,
    description: 'Ultra-lightweight moisture-control workout t-shirt designed for maximum athletic performance.',
    tag: 'Quick Dry',
    sizes: ['S', 'M', 'L', 'XL'],
    colors: ['Navy', 'Charcoal', 'White'],
    imageUrl: '/images/prod-sports-tee.jpg',
    image: '/images/prod-sports-tee.jpg',
    images: ['/images/prod-sports-tee.jpg'],
    specifications: [
      { key: 'Fabric Composition', value: '100% Moisture-Wicking Performance Polyester' },
      { key: 'Fabric Weight', value: '180 GSM' },
      { key: 'Fabric Pattern', value: 'Dotknit Micro-Mesh' },
      { key: 'Fabric Structure', value: 'Breathable Interlock' },
      { key: 'Fabric Finish & Treatments', value: 'Anti-Wicking, Anti-Microbial, Anti-Static, UPF 50+ UV Guard' },
      { key: 'Style', value: 'Ergonomic Athletic Fit' },
      { key: 'Sleeve Type', value: 'Half Sleeve with Raglan Cut' },
      { key: 'Garment Finishing', value: 'Flatlock Anti-Chafing Seams, Reflective Accents' }
    ],
    rating: 4.8,
    reviews: 29
  },
  {
    id: 'db-11',
    name: 'Velocity Micro-Mesh Training Jersey',
    price: 1399,
    category: 'Sports Jerseys',
    gender: 'Sports',
    stock: 20,
    description: 'Pro grade breathable micro-mesh training jersey with anti-chafing flatlock stitching.',
    tag: 'Unisex Drop',
    sizes: ['S', 'M', 'L', 'XL'],
    colors: ['Royal Blue', 'Black', 'Red'],
    imageUrl: '/images/cat-slide-sports-jersey.jpg',
    image: '/images/cat-slide-sports-jersey.jpg',
    images: ['/images/cat-slide-sports-jersey.jpg'],
    specifications: [
      { key: 'Fabric Composition', value: '100% Performance Micro-Mesh Polyester' },
      { key: 'Fabric Weight', value: '190 GSM' },
      { key: 'Fabric Pattern', value: 'Rise Knit / Dotknit' },
      { key: 'Fabric Structure', value: 'Interlock Airflow Grid' },
      { key: 'Fabric Finish & Treatments', value: 'Anti-Wicking, Anti-Static, Quick-Dry Treatment' },
      { key: 'Style', value: 'Regular Pro Jersey Fit' },
      { key: 'Sleeve Type', value: 'Half Sleeve' },
      { key: 'Garment Finishing', value: 'Side-Seamed, Double Needle Stitched Sleeves & Hem' }
    ],
    rating: 4.9,
    reviews: 38
  },
  {
    id: 'db-10',
    name: 'Aeroready Dry-Fit Sports T-Shirt',
    price: 999,
    category: 'Sports T-Shirts',
    gender: 'Sports',
    stock: 35,
    description: 'High-density moisture-wicking athletic performance sports t-shirt engineered for workouts and movement.',
    tag: 'Performance',
    sizes: ['S', 'M', 'L', 'XL'],
    colors: ['Black', 'Royal Blue', 'Gray'],
    imageUrl: '/images/prod-sports-tee.jpg',
    image: '/images/prod-sports-tee.jpg',
    images: ['/images/prod-sports-tee.jpg'],
    specifications: [
      { key: 'Fabric Composition', value: '100% Poly-Spandex Quick-Dry Blend' },
      { key: 'Fabric Weight', value: '180 GSM' },
      { key: 'Fabric Pattern', value: 'Solid Melange' },
      { key: 'Fabric Structure', value: 'Single Knit Micro-Interlock' },
      { key: 'Fabric Finish & Treatments', value: 'Anti-Microbial, Anti-Odor, UPF 50+ Sun Protection' },
      { key: 'Style', value: 'Athletic Motion Fit' },
      { key: 'Sleeve Type', value: 'Half Sleeve' },
      { key: 'Garment Finishing', value: 'Reinforced Flatlock Seams, Stretch Neckline' }
    ],
    rating: 4.8,
    reviews: 32
  },
  {
    id: 'db-9',
    name: 'Pro Elite Breathable Sports Jersey',
    price: 1499,
    category: 'Sports Jerseys',
    gender: 'Sports',
    stock: 25,
    description: 'Engineered activewear sports jersey with micro-mesh airflow panels and ergonomic seams.',
    tag: 'New Drop',
    sizes: ['S', 'M', 'L', 'XL'],
    colors: ['Black', 'Neon Green', 'White'],
    imageUrl: '/images/cat-slide-sports-jersey.jpg',
    image: '/images/cat-slide-sports-jersey.jpg',
    images: ['/images/cat-slide-sports-jersey.jpg'],
    specifications: [
      { key: 'Fabric Composition', value: '100% Hydro-Cool Polyester' },
      { key: 'Fabric Weight', value: '190 GSM' },
      { key: 'Fabric Pattern', value: 'Honeycomb Micro-Mesh' },
      { key: 'Fabric Structure', value: 'Dual-Layer Knit' },
      { key: 'Fabric Finish & Treatments', value: 'Anti-Wicking, Anti-Static, UPF 50+ UV Guard' },
      { key: 'Style', value: 'Pro Team Athletic Cut' },
      { key: 'Sleeve Type', value: 'Half Sleeve' },
      { key: 'Garment Finishing', value: 'Dynamic Contrast Panels, Heavy Duty Seams' }
    ],
    rating: 4.9,
    reviews: 45
  },
  {
    id: 'db-8',
    name: 'Luxe Signature Oversized Tee',
    price: 1299,
    category: 'Oversized T-Shirts',
    gender: 'Womens',
    stock: 40,
    description: '240 GSM organic combed cotton relaxed silhouette oversized tee with soft bio-wash finish.',
    tag: 'Trending',
    sizes: ['XS', 'S', 'M', 'L'],
    colors: ['Beige', 'Lavender', 'Off-White'],
    imageUrl: '/images/cat-slide-women-classic.jpg',
    image: '/images/cat-slide-women-classic.jpg',
    images: ['/images/cat-slide-women-classic.jpg'],
    specifications: [
      { key: 'Fabric Composition', value: '100% Combed Ring-Spun Organic Cotton' },
      { key: 'Fabric Weight', value: '240 GSM (Heavyweight Luxury Drape)' },
      { key: 'Fabric Pattern', value: 'Solid Pure Matte' },
      { key: 'Fabric Structure', value: 'Single Jersey Knit' },
      { key: 'Fabric Finish & Treatments', value: 'Pre-Shrunk & Bio-Washed, Peach-Skin Hand Feel' },
      { key: 'Style', value: 'Oversized Drop Shoulder Boxy Silhouette' },
      { key: 'Sleeve Type', value: 'Half Sleeve / Relaxed Extended' },
      { key: 'Garment Finishing', value: 'Double-Needle Bound Collar, Sleeves & Hem' }
    ],
    rating: 4.7,
    reviews: 28
  },
  {
    id: 'db-7',
    name: 'Relaxed Linen Drop Shirt',
    price: 1799,
    category: 'Shirts',
    gender: 'Womens',
    stock: 20,
    description: 'Breathable linen-cotton blended shirt with tailored cuffs and loose drape.',
    tag: 'Essential',
    sizes: ['S', 'M', 'L'],
    colors: ['Sage Green', 'White'],
    imageUrl: '/images/cat-slide-women-classic.jpg',
    image: '/images/cat-slide-women-classic.jpg',
    images: ['/images/cat-slide-women-classic.jpg'],
    specifications: [
      { key: 'Fabric Composition', value: '70% Combed Cotton / 30% Pure Natural Linen' },
      { key: 'Fabric Weight', value: '170 GSM' },
      { key: 'Fabric Pattern', value: 'Subtle Linen Weave' },
      { key: 'Fabric Structure', value: 'Woven Poplin Blend' },
      { key: 'Fabric Finish & Treatments', value: 'Soft Enzyme Washed for Pill Resistance' },
      { key: 'Style', value: 'Relaxed Drop Button-Down Shirt' },
      { key: 'Sleeve Type', value: 'Long Sleeve with Button Cuffs' },
      { key: 'Garment Finishing', value: 'Spread Collar, Curved Shirt-Tail Hem, Pearlized Buttons' }
    ],
    rating: 4.6,
    reviews: 19
  },
  {
    id: 'db-6',
    name: 'Velvet Crop Heavyweight Hoodie',
    price: 2499,
    category: 'Hoodies',
    gender: 'Womens',
    stock: 18,
    description: '380 GSM plush fleece cropped hoodie with ribbed hem and drawstrings.',
    tag: 'Bestseller',
    sizes: ['S', 'M', 'L'],
    colors: ['Dusty Rose', 'Charcoal'],
    imageUrl: '/images/cat-slide-women-hoodies.jpg',
    image: '/images/cat-slide-women-hoodies.jpg',
    images: ['/images/cat-slide-women-hoodies.jpg'],
    specifications: [
      { key: 'Fabric Composition', value: '100% Combed Cotton 20s Count Yarn' },
      { key: 'Fabric Weight', value: '380 GSM Ultra-Heavyweight' },
      { key: 'Fabric Pattern', value: 'Solid Brushed Velvet Touch' },
      { key: 'Fabric Structure', value: 'Plush Brushed Fleece' },
      { key: 'Fabric Finish & Treatments', value: 'Bio-Washed, Eco-Friendly Dyes, Bit Washed for 100% Shrinkage Arrest' },
      { key: 'Style', value: 'Cropped Boxy Fit Hoodie' },
      { key: 'Sleeve Type', value: 'Full Sleeve with Ribbed Cuffs' },
      { key: 'Garment Finishing', value: 'Double-Lined Hood, Heavy Nickel-Free Metal Tip Drawstrings, 5-Thread Finish' }
    ],
    rating: 4.9,
    reviews: 52
  },
  {
    id: 'db-polo-everyday-women',
    name: 'Everyday Tailored Knit Polo',
    price: 1599,
    category: 'Polos',
    poloType: 'Everyday Polo',
    gender: 'Womens',
    stock: 22,
    description: 'Fine-knit breathable everyday combed cotton polo with minimalist contrast tipping.',
    tag: 'Daily Essential',
    sizes: ['S', 'M', 'L'],
    colors: ['Cream', 'Black'],
    imageUrl: '/images/cat-slide-women-polo.jpg',
    image: '/images/cat-slide-women-polo.jpg',
    images: ['/images/cat-slide-women-polo.jpg'],
    specifications: [
      { key: 'Fabric Composition', value: '100% Combed Pique Cotton' },
      { key: 'Fabric Weight', value: '220 GSM' },
      { key: 'Fabric Pattern', value: 'Micro-Pique Knit with Contrast Tipping' },
      { key: 'Fabric Structure', value: 'Airtex Honeycomb' },
      { key: 'Fabric Finish & Treatments', value: 'Mercerized Bio-Wash, Anti-Pilling Guard' },
      { key: 'Style', value: 'Tailored Feminine Everyday Polo Fit' },
      { key: 'Sleeve Type', value: 'Half Sleeve with Ribbed Tipped Cuffs' },
      { key: 'Garment Finishing', value: 'Side Slits, 3-Button Placket with Engraved Buttons' }
    ],
    rating: 4.8,
    reviews: 24
  },
  {
    id: 'db-polo-blend-women',
    name: 'Luxe Modal-Cotton Blended Polo',
    price: 1899,
    category: 'Polos',
    poloType: 'Blended Polo',
    gender: 'Womens',
    stock: 20,
    description: 'Ultra-luxurious Supima cotton & micro-modal blended knit polo with liquid drape and silk hand-feel.',
    tag: 'Silk Blend',
    sizes: ['S', 'M', 'L'],
    colors: ['Champagne', 'Midnight Blue'],
    imageUrl: '/images/cat-slide-women-polo.jpg',
    image: '/images/cat-slide-women-polo.jpg',
    images: ['/images/cat-slide-women-polo.jpg'],
    specifications: [
      { key: 'Fabric Composition', value: '60% Cotton, 40% Polyester' },
      { key: 'Fabric Weight', value: '240 GSM' },
      { key: 'Fabric Pattern', value: 'Solid' },
      { key: 'Fabric Structure', value: 'Airtex' },
      { key: 'Fabric Finish and Treatments', value: 'NA' },
      { key: 'Style', value: 'Regular fit Polo' },
      { key: 'Sleeve Type', value: 'Half Sleeve' },
      { key: 'Garment Finishing', value: 'Side-Seamed, Double Needle Stitched Sleeves and Bottom Hem, Forward Shoulder.' }
    ],
    rating: 4.9,
    reviews: 31
  },
  {
    id: 'db-polo-comfort-women',
    name: 'Cloud-Touch Comfort Knit Polo',
    price: 1099,
    category: 'Polos',
    poloType: 'Comfort Polo',
    gender: 'Womens',
    stock: 25,
    description: 'Ultra-soft peach-finish bio-washed luxury comfort polo with 4-way stretch and relaxed silhouette.',
    tag: 'Cloud Comfort',
    sizes: ['S', 'M', 'L'],
    colors: ['Blush Pink', 'Off-White'],
    imageUrl: '/images/cat-slide-women-polo.jpg',
    image: '/images/cat-slide-women-polo.jpg',
    images: ['/images/cat-slide-women-polo.jpg'],
    specifications: [
      { key: 'Fabric Composition', value: '100% Combed Cotton 20s Count yarn' },
      { key: 'Fabric Weight', value: '270 GSM' },
      { key: 'Fabric Pattern', value: 'Solid' },
      { key: 'Fabric Structure', value: 'Airtex' },
      { key: 'Fabric Finish and Treatments', value: 'Bio-Washed, Eco-Friendly Dyes with Colorfastness, Bit Washed for 100% Shrinkage arrest.' },
      { key: 'Style', value: 'Polo Regular fit' },
      { key: 'Sleeve Type', value: 'Half Sleeve' },
      { key: 'Garment Finishing', value: 'Side-Seamed, Double Needle Stitched Sleeves and Bottom Hem, Box placket with slit opens.' }
    ],
    rating: 4.9,
    reviews: 42
  },
  {
    id: 'db-4',
    name: 'Acid Wash 240 GSM Oversized Tee',
    price: 1199,
    category: 'Oversized T-Shirts',
    gender: 'Mens',
    stock: 45,
    description: 'Heavyweight premium cotton tee with authentic acid-wash texture and dropped shoulders.',
    tag: 'New',
    sizes: ['M', 'L', 'XL', 'XXL'],
    colors: ['Washed Gray', 'Vintage Black'],
    imageUrl: '/images/prod-acid-wash-tee.jpg',
    image: '/images/prod-acid-wash-tee.jpg',
    images: ['/images/prod-acid-wash-tee.jpg'],
    specifications: [
      { key: 'Fabric Composition', value: '100% Premium Heavyweight Combed Cotton' },
      { key: 'Fabric Weight', value: '240 GSM' },
      { key: 'Fabric Pattern', value: 'Authentic Mineral Acid-Wash' },
      { key: 'Fabric Structure', value: 'Single Jersey' },
      { key: 'Fabric Finish & Treatments', value: 'Pre-Shrunk Bio-Enzyme Washed for Vintage Hand Feel' },
      { key: 'Style', value: 'Streetwear Oversized Drop-Shoulder' },
      { key: 'Sleeve Type', value: 'Half Sleeve' },
      { key: 'Garment Finishing', value: 'High-Density 1x1 Ribbed Collar, Twin Needle Flat Hem' }
    ],
    rating: 4.7,
    reviews: 36
  },
  {
    id: 'db-3',
    name: 'Structured Utility Oxford Shirt',
    price: 1899,
    category: 'Shirts',
    gender: 'Mens',
    stock: 30,
    description: 'Tailored streetwear oxford button-down shirt with dual flap pockets.',
    tag: 'Signature',
    sizes: ['M', 'L', 'XL'],
    colors: ['White', 'Olive', 'Navy'],
    imageUrl: '/images/cat-slide-men-classic.jpg',
    image: '/images/cat-slide-men-classic.jpg',
    images: ['/images/cat-slide-men-classic.jpg'],
    specifications: [
      { key: 'Fabric Composition', value: '100% High-Grade Oxford Cotton' },
      { key: 'Fabric Weight', value: '180 GSM' },
      { key: 'Fabric Pattern', value: 'Classic Pinpoint Oxford Weave' },
      { key: 'Fabric Structure', value: 'Structured Woven Oxford' },
      { key: 'Fabric Finish & Treatments', value: 'Easy-Care Bio-Wash, Colorfast Treatment' },
      { key: 'Style', value: 'Streetwear Button-Down Regular Fit' },
      { key: 'Sleeve Type', value: 'Full Sleeve with Adjustable Button Cuffs' },
      { key: 'Garment Finishing', value: 'Dual Flap Utility Chest Pockets, Button-Down Collar, Reinforced Pearlized Buttons' }
    ],
    rating: 4.8,
    reviews: 30
  },
  {
    id: 'db-2',
    name: 'Urban Heavyweight Hoodie',
    price: 4499,
    category: 'Hoodies',
    gender: 'Mens',
    stock: 15,
    description: '400 GSM brushed fleece streetwear hoodie with double-lined hood.',
    tag: 'Trending',
    sizes: ['M', 'L', 'XL'],
    colors: ['Black', 'Off-White'],
    imageUrl: '/images/cat-slide-men-hoodie.jpg',
    image: '/images/cat-slide-men-hoodie.jpg',
    images: ['/images/cat-slide-men-hoodie.jpg'],
    specifications: [
      { key: 'Fabric Composition', value: '100% Combed Cotton 20s Count Yarn' },
      { key: 'Fabric Weight', value: '400 GSM Ultra-Heavyweight' },
      { key: 'Fabric Pattern', value: 'Solid Deep Dye' },
      { key: 'Fabric Structure', value: 'Brushed Fleece Interior' },
      { key: 'Fabric Finish & Treatments', value: 'Bio-Washed, Bit Washed for 100% Shrinkage Arrest' },
      { key: 'Style', value: 'Hooded Relaxed Streetwear Fit' },
      { key: 'Sleeve Type', value: 'Full Sleeve with Heavy 2x2 Rib Cuffs' },
      { key: 'Garment Finishing', value: 'Side-Seamed, Seamless Front Kangaroo Pocket, Nickel-Free Hardware, 5-Thread Coverstitch' }
    ],
    rating: 4.9,
    reviews: 64
  },
  {
    id: 'db-1',
    name: 'Midnight Silk Blended Polo',
    price: 799,
    category: 'Polos',
    poloType: 'Blended Polo',
    gender: 'Mens',
    stock: 25,
    description: 'Handcrafted luxury breathable blended polo with signature double-needle stitching and crisp silhouette.',
    tag: 'Silk Blend',
    sizes: ['S', 'M', 'L', 'XL'],
    colors: ['Black', 'Navy'],
    imageUrl: '/images/cat-slide-men-polo.jpg',
    image: '/images/cat-slide-men-polo.jpg',
    images: ['/images/cat-slide-men-polo.jpg'],
    specifications: [
      { key: 'Fabric Composition', value: '60% Cotton, 40% Polyester' },
      { key: 'Fabric Weight', value: '240 GSM' },
      { key: 'Fabric Pattern', value: 'Solid' },
      { key: 'Fabric Structure', value: 'Airtex' },
      { key: 'Fabric Finish and Treatments', value: 'NA' },
      { key: 'Style', value: 'Regular fit Polo' },
      { key: 'Sleeve Type', value: 'Half Sleeve' },
      { key: 'Garment Finishing', value: 'Side-Seamed, Double Needle Stitched Sleeves and Bottom Hem, Forward Shoulder.' }
    ],
    rating: 4.9,
    reviews: 80
  },
  {
    id: 'db-polo-everyday-men',
    name: 'Everyday Classic Pique Polo',
    price: 699,
    category: 'Polos',
    poloType: 'Everyday Polo',
    gender: 'Mens',
    stock: 35,
    description: 'Timeless 200 GSM Airtex polo engineered for everyday luxury, breathable comfort, and all-day crisp silhouette.',
    tag: 'Daily Essential',
    sizes: ['S', 'M', 'L', 'XL', 'XXL'],
    colors: ['White', 'Navy', 'Olive', 'Charcoal'],
    imageUrl: '/images/cat-slide-men-polo.jpg',
    image: '/images/cat-slide-men-polo.jpg',
    images: ['/images/cat-slide-men-polo.jpg'],
    specifications: [
      { key: 'Fabric Composition', value: '100% Polyester' },
      { key: 'Fabric Weight', value: '200 GSM' },
      { key: 'Fabric Pattern', value: 'Solid' },
      { key: 'Fabric Structure', value: 'Airtex' },
      { key: 'Fabric Finish and Treatments', value: 'Anti-wicking, Anti Static, Anti-Microbial, and UV UPF 50+ Treatment done.' },
      { key: 'Style', value: 'Regular fit Polo' },
      { key: 'Sleeve Type', value: 'Half Sleeve' },
      { key: 'Garment Finishing', value: 'Side-Seamed, Double Needle Stitched Sleeves and Bottom Hem, Forward Shoulder.' }
    ],
    rating: 4.8,
    reviews: 58
  },
  {
    id: 'db-polo-comfort-men',
    name: 'Ultra-Soft Comfort Stretch Polo',
    price: 1099,
    category: 'Polos',
    poloType: 'Comfort Polo',
    gender: 'Mens',
    stock: 28,
    description: 'Engineered 270 GSM combed cotton comfort polo with Airtex structure, bio-washed shrinkage arrest and box placket for absolute day-long ease.',
    tag: 'Cloud Comfort',
    sizes: ['S', 'M', 'L', 'XL'],
    colors: ['Heather Gray', 'Black', 'Steel Blue'],
    imageUrl: '/images/cat-slide-men-polo.jpg',
    image: '/images/cat-slide-men-polo.jpg',
    images: ['/images/cat-slide-men-polo.jpg'],
    specifications: [
      { key: 'Fabric Composition', value: '100% Combed Cotton 20s Count yarn' },
      { key: 'Fabric Weight', value: '270 GSM' },
      { key: 'Fabric Pattern', value: 'Solid' },
      { key: 'Fabric Structure', value: 'Airtex' },
      { key: 'Fabric Finish and Treatments', value: 'Bio-Washed, Eco-Friendly Dyes with Colorfastness, Bit Washed for 100% Shrinkage arrest.' },
      { key: 'Style', value: 'Polo Regular fit' },
      { key: 'Sleeve Type', value: 'Half Sleeve' },
      { key: 'Garment Finishing', value: 'Side-Seamed, Double Needle Stitched Sleeves and Bottom Hem, Box placket with slit opens.' }
    ],
    rating: 4.9,
    reviews: 47
  }
];

const DEFAULT_ORDERS = [];

const DEFAULT_VIDEOS = [
  {
    id: 1,
    title: "The Jersey Collection",
    subtitle: "240+ GSM Organic Combed Cotton • Clean Fit",
    src: "https://cdn.coverr.co/videos/coverr-fashion-model-in-streetwear-5421/1080p.mp4",
    poster: "/images/collection-classic.png",
    tag: "FLAGSHIP REEL"
  },
  {
    id: 2,
    title: "The Women Collection",
    subtitle: "Fresh Colour • Elevated Everyday Silhouette",
    src: "https://cdn.coverr.co/videos/coverr-fashion-portrait-of-a-model-4768/1080p.mp4",
    poster: "/images/collection-studio.png",
    tag: "LOOKBOOK"
  },
  {
    id: 3,
    title: "The Men Collection",
    subtitle: "Graphic Print • Modern Urban Drape",
    src: "https://cdn.coverr.co/videos/coverr-young-stylish-man-walking-in-the-city-4521/1080p.mp4",
    poster: "/images/collection-statement.png",
    tag: "NEW DROP"
  },
  {
    id: 4,
    title: "Heavyweight Squad Drape",
    subtitle: "Double-Stitched Seams & Custom Trims",
    src: "https://cdn.coverr.co/videos/coverr-model-wearing-a-hoodie-and-sunglasses-4821/1080p.mp4",
    poster: "/images/hero-slide-2.png",
    tag: "BESTSELLER"
  },
  {
    id: 5,
    title: "Studio Craft & Texture",
    subtitle: "Tactile Luxury Everyday Drapes",
    src: "https://cdn.coverr.co/videos/coverr-a-stylish-model-posing-in-a-studio-7935/1080p.mp4",
    poster: "/images/hero-slide-1.png",
    tag: "ESSENTIAL"
  }
];

const DEFAULT_CONTRIBUTORS = [
  {
    id: 1,
    name: 'Jane Doe',
    role: 'Senior Fashion Editor & Trend Forecaster',
    bio: 'Exploring the intersection of modern luxury streetwear, sustainable textiles, and contemporary minimalist silhouettes.',
    avatar_url: '/images/blog-author-jane.jpg',
    read_more_link: '#blog?author=jane-doe'
  },
  {
    id: 2,
    name: 'John Doe',
    role: 'Creative Director & Apparel Architect',
    bio: 'Specializing in heavyweight fabric construction, precision drop-tail cuts, and high-density 240+ GSM Portuguese combed cotton craftsmanship.',
    avatar_url: '/images/blog-author-john.jpg',
    read_more_link: '#blog?author=john-doe'
  }
];

const DEFAULT_BLOGS = [
  {
    id: 1,
    title: 'The Streetwear Renaissance: Crafting Heavyweight Essentials with Timeless Drapes',
    slug: 'the-streetwear-renaissance-heavyweight-essentials',
    category: 'Editorial Spotlight',
    excerpt: 'Lorem ipsum dolor sit amet, consectetuer adipiscing elit, sed diam nonummy nibh euismod tincidunt ut laoreet dolore magna aliquam erat volutpat. Ut wisi enim ad minim veniam, quis nostrud exerci tation ullamcorper suscipit.',
    content: `# The Streetwear Renaissance: Crafting Heavyweight Essentials with Timeless Drapes\n\nStreetwear has transcended temporary counter-culture trends to establish itself as the foundation of modern high luxury fashion. At PallyWear, we believe that true wardrobe longevity begins at the fiber level.\n\n## The Architecture of Heavyweight Cotton\n\nWhen constructing our flagship collection, we chose a **240+ GSM organic combed cotton** base. Unlike lightweight fast-fashion fabrics that lose their silhouette after few washes, high-density cotton holds a relaxed, architectural drape that flatters any body type.\n\n> "A great garment does not scream for attention; it commands respect through its proportion, weight, and subtle texture."\n\n### Key Craftsmanship Highlights:\n- **Pre-Shrunk Bio-Wash Treatment**: Prevents shrinkage while bestowing a luxurious peach-skin hand feel.\n- **Double-Needle Flatlock Seaming**: Reinforced stress points built for active urban lifestyles.\n- **Zero Synthetic Fillers**: 100% breathable organic fibers ensuring year-round temperature regulation.\n\n## Styling the Modern Silhouette\n\nPairing an oversized drop-shoulder hoodie with tailored trousers or structured shorts creates a balanced, elevated aesthetic suitable for both creative office spaces and weekend getaways.`,
    image_url: '/images/blog-hero-model.jpg',
    author_name: 'PallyWear Editorial Team',
    author_role: 'Design & Sourcing Desk',
    author_avatar: '/images/blog-author-jane.jpg',
    publish_date: '12 December 2024',
    read_time: '5 min read',
    is_hero: 1,
    is_published: 1,
    tags: ['Streetwear', 'Luxury', 'Design', 'Heavyweight Cotton']
  },
  {
    id: 2,
    title: 'Minimalism Meets Performance: The New Standard in Active Streetwear',
    slug: 'minimalism-meets-performance-active-streetwear',
    category: 'Trend Analysis',
    excerpt: 'Lorem ipsum dolor sit amet, consectetuer adipiscing elit, sed diam nonummy nibh euismod tincidunt ut laoreet dolore magna aliquam erat volutpat.',
    content: `# Minimalism Meets Performance: The New Standard in Active Streetwear\n\nThe boundary between technical sportswear and high-street fashion is disappearing. Consumers no longer want to choose between high-performance moisture control and refined minimalist styling.\n\n## Engineered for Movement\n\nOur latest sports drop integrates micro-mesh ventilation with clean, tonal branding. Whether in the gym, on the track, or navigating city transit, performance activewear should move effortlessly with your rhythm.\n\n### What to Look For:\n1. **Four-way mechanical stretch** without synthetic stiffness.\n2. **Ergonomic flatlock stitching** that eliminates friction.\n3. **Deep, muted color palettes** that pair naturally with daily casuals.`,
    image_url: '/images/blog-article-1.jpg',
    author_name: 'Jane Doe',
    author_role: 'Senior Fashion Editor',
    author_avatar: '/images/blog-author-jane.jpg',
    publish_date: '12 December 2024',
    read_time: '4 min read',
    is_hero: 0,
    is_published: 1,
    tags: ['Performance', 'Sportswear', 'Minimalism']
  },
  {
    id: 3,
    title: 'Colour Psychology in Urban Fashion: Why Vibrant Accents Matter',
    slug: 'colour-psychology-in-urban-fashion',
    category: 'Style Guide',
    excerpt: 'Lorem ipsum dolor sit amet, consectetuer adipiscing elit, sed diam nonummy nibh euismod tincidunt ut laoreet dolore magna aliquam erat volutpat.',
    content: `# Colour Psychology in Urban Fashion: Why Vibrant Accents Matter\n\nWhile monochromatic black and neutral tones remain classic streetwear pillars, deliberate injections of energetic color bring personality and emotional warmth to everyday wardrobes.\n\n## The Power of Warm Amber & Mustard\n\nWarm mustard and vibrant tones elevate neutral coats and denim, creating visual focal points that communicate confidence and approachable creativity.\n\n> "Color is an expression of energy. Adding a statement piece transforms an entire look from ordinary to memorable."\n\n### 3 Ways to Introduce Color:\n- **Layering**: A pop of color underneath an open dark jacket.\n- **Accessory Tonal Pairing**: Matching caps or socks with graphic accents.\n- **Standout Outerwear**: Letting a bold fleece or jacket take center stage.`,
    image_url: '/images/blog-article-2.jpg',
    author_name: 'John Doe',
    author_role: 'Creative Director',
    author_avatar: '/images/blog-author-john.jpg',
    publish_date: '14 December 2024',
    read_time: '3 min read',
    is_hero: 0,
    is_published: 1,
    tags: ['Color Theory', 'Styling', 'Urban Style']
  },
  {
    id: 4,
    title: 'Inside the Studio: Sustainable Sourcing and Ethical Craftsmanship',
    slug: 'inside-the-studio-sustainable-sourcing-craftsmanship',
    category: 'Behind The Scenes',
    excerpt: 'Lorem ipsum dolor sit amet, consectetuer adipiscing elit, sed diam nonummy nibh euismod tincidunt ut laoreet dolore magna aliquam erat volutpat.',
    content: `# Inside the Studio: Sustainable Sourcing and Ethical Craftsmanship\n\nBehind every clean stitch and finished seam lies a commitment to responsible production. We take you inside our design workshop and partner mills.\n\n## From Raw Fiber to Finished Drop\n\nOur production partners adhere to rigorous environmental and fair-wage standards. By eliminating toxic runoff and choosing bio-enzyme washing methods, we minimize our ecological footprint while delivering apparel designed to last for years.\n\n## The Collaborative Spirit\n\nEvery collection is born from open collaboration between textile engineers, graphic artists, and pattern makers. We test every prototype in real-world conditions before a drop goes live.`,
    image_url: '/images/blog-article-3.jpg',
    author_name: 'PallyWear Team',
    author_role: 'Sustainability Desk',
    author_avatar: '/images/blog-author-jane.jpg',
    publish_date: '18 December 2024',
    read_time: '6 min read',
    is_hero: 0,
    is_published: 1,
    tags: ['Sustainability', 'Craftsmanship', 'Studio']
  }
];

export const ShopProvider = ({ children }) => {
  // Theme state
  const [darkMode, setDarkMode] = useState(() => {
    try {
      const saved = localStorage.getItem('pallywear_dark');
      return saved ? JSON.parse(saved) : false;
    } catch (e) {
      return false;
    }
  });

  // Authentication state
  const [user, setUser] = useState(() => {
    try {
      const saved = localStorage.getItem('pallywear_user');
      return saved ? JSON.parse(saved) : null;
    } catch (e) {
      return null;
    }
  });
  const [authModalOpen, setAuthModalOpen] = useState(false);

  // Helper to resolve active view from browser URL path or hash
  const getViewFromLocation = useCallback(() => {
    if (typeof window === 'undefined') return 'home';
    const rawHash = (window.location.hash || '').replace(/^#\/?/, '').toLowerCase();
    const hash = rawHash.split('?')[0];
    const pathname = (window.location.pathname || '').replace(/^\//, '').toLowerCase().split('/')[0];
    
    const target = pathname || hash;
    if (
      target === 'shop' || target.startsWith('shop/') ||
      target === 'catalog' || target.startsWith('catalog/') ||
      target === 'products' || target.startsWith('products/')
    ) {
      return 'shop';
    }
    if (target === 'sitemap' || target === 'site-map') return 'sitemap';
    if (target === 'about' || target === 'our-story') return 'about';
    if (target === 'blog' || target === 'blogs' || target === 'journal') return 'blog';
    if (target === 'blog-post' || target === 'article' || target === 'post') return 'blog-post';
    if (target === 'account' || target === 'profile' || target === 'orders') return 'account';
    if (target === 'track-order' || target === 'track' || target === 'tracking') return 'track-order';
    if (target === 'checkout' || target === 'cart') return 'checkout';
    if (target === 'admin') return 'admin';
    if (target === 'product-detail' || target === 'product') return 'product-detail';
    return 'home';
  }, []);

  // Navigation & Product / Blog details state — initialized from browser URL
  const [activeView, setActiveView] = useState(() => getViewFromLocation());
  const [activeProduct, setActiveProduct] = useState(null);
  const [activeBlogPost, setActiveBlogPost] = useState(null);

  // Cart & Inventory state — initialize from localStorage if available
  const [products, setProducts] = useState(() => {
    try {
      const savedVer = localStorage.getItem('pallywear_catalog_version');
      if (savedVer === CATALOG_VERSION) {
        const saved = localStorage.getItem('pallywear_products');
        if (saved) {
          const parsed = JSON.parse(saved);
          if (Array.isArray(parsed) && parsed.length > 0) {
            return sanitizeProductsForStorage(parsed);
          }
        }
      }
    } catch (e) {
      console.warn('Error reading stored products:', e);
    }
    return INITIAL_PRODUCTS;
  });

  // Blogs state
  const [blogs, setBlogs] = useState(() => {
    try {
      const saved = localStorage.getItem('pallywear_blogs');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed;
        }
      }
    } catch (e) {
      console.warn('Error reading stored blogs:', e);
    }
    return DEFAULT_BLOGS;
  });

  // Contributors state
  const [contributors, setContributors] = useState(() => {
    try {
      const saved = localStorage.getItem('pallywear_contributors');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed;
        }
      }
    } catch (e) {
      console.warn('Error reading stored contributors:', e);
    }
    return DEFAULT_CONTRIBUTORS;
  });

  useEffect(() => {
    try {
      localStorage.setItem('pallywear_blogs', JSON.stringify(blogs));
    } catch (e) {}
  }, [blogs]);

  useEffect(() => {
    try {
      localStorage.setItem('pallywear_contributors', JSON.stringify(contributors));
    } catch (e) {}
  }, [contributors]);

  useEffect(() => {
    try {
      localStorage.setItem('pallywear_products', JSON.stringify(sanitizeProductsForStorage(products)));
    } catch (e) {
      console.warn('Could not sync products to localStorage:', e.message);
    }
  }, [products]);

  // eslint-disable-next-line no-unused-vars
  const [productsLoadedFromApi, setProductsLoadedFromApi] = useState(false);

  const formatApiProduct = useCallback((item) => {
    let parsedSizes = ['S', 'M', 'L', 'XL'];
    if (Array.isArray(item.sizes)) {
      parsedSizes = item.sizes;
    } else if (typeof item.sizes === 'string') {
      try { parsedSizes = JSON.parse(item.sizes); } catch(e) { parsedSizes = ['S', 'M', 'L', 'XL']; }
    }

    let parsedColors = ['Black', 'White'];
    if (Array.isArray(item.colors)) {
      parsedColors = item.colors;
    } else if (typeof item.colors === 'string') {
      try { parsedColors = JSON.parse(item.colors); } catch(e) { parsedColors = ['Black', 'White']; }
    }

    let parsedImages = [];
    if (Array.isArray(item.images)) {
      parsedImages = item.images;
    } else if (typeof item.images === 'string') {
      try { parsedImages = JSON.parse(item.images); } catch(e) { parsedImages = []; }
    }
    if (!parsedImages.length) {
      parsedImages = [item.imageUrl || item.image_url || '/images/dragon-polo.png'];
    }

    let parsedSpecs = [];
    if (Array.isArray(item.specifications)) {
      parsedSpecs = item.specifications;
    } else if (typeof item.specifications === 'string') {
      try { parsedSpecs = JSON.parse(item.specifications); } catch(e) { parsedSpecs = []; }
    } else if (typeof item.specifications === 'object' && item.specifications !== null) {
      parsedSpecs = item.specifications;
    }

    const mainImg = item.imageUrl || item.image_url || parsedImages[0] || '/images/dragon-polo.png';
    let prodCategory = item.category || 'Polos';
    let prodGender = item.gender || 'Mens';

    const pName = (item.name || '').toLowerCase();
    if (prodCategory === 'Polos' && (pName.includes('jersey') || prodGender === 'Sports')) {
      prodCategory = 'Sports Jerseys';
      prodGender = 'Sports';
    } else if (prodCategory === 'Sports Jerseys' || prodCategory === 'Sports T-Shirts') {
      prodGender = 'Sports';
    }

    const isPolo = prodCategory.toLowerCase().includes('polo') || (item.name && item.name.toLowerCase().includes('polo'));
    const resolvedPoloType = isPolo ? resolveProductPoloType(item) : (item.poloType || item.polo_type || '');

    return {
      id: `db-${item.id}`,
      name: item.name,
      category: prodCategory,
      poloType: resolvedPoloType,
      gender: prodGender,
      description: item.description || '',
      price: Number(item.price) || 0,
      rating: item.rating !== undefined ? Number(item.rating) : 4.5,
      reviews: item.reviews !== undefined ? Number(item.reviews) : 20,
      stock: item.stock !== undefined ? Number(item.stock) : 10,
      tag: item.tag || 'New',
      sizes: parsedSizes,
      colors: parsedColors,
      imageUrl: mainImg,
      image: getProductImage(mainImg, prodCategory),
      images: parsedImages.map(img => getProductImage(img, prodCategory)),
      specifications: parsedSpecs
    };
  }, []);

  const refreshDbData = useCallback(async () => {
    try {
      const [apiProducts, apiOrders, apiVideos, apiBlogs, apiContributors] = await Promise.allSettled([
        fetchProductsApi(),
        fetchOrdersApi(),
        fetchVideosApi(),
        fetchBlogsApi(),
        fetchContributorsApi()
      ]);

      if (apiProducts.status === 'fulfilled' && Array.isArray(apiProducts.value) && apiProducts.value.length > 0) {
        const formatted = apiProducts.value.map(formatApiProduct);
        setProducts(formatted);
      }

      if (apiOrders.status === 'fulfilled' && Array.isArray(apiOrders.value)) {
        setOrders(apiOrders.value);
      }

      if (apiVideos.status === 'fulfilled' && Array.isArray(apiVideos.value) && apiVideos.value.length > 0) {
        setVideos(apiVideos.value);
      }

      if (apiBlogs.status === 'fulfilled' && Array.isArray(apiBlogs.value) && apiBlogs.value.length > 0) {
        setBlogs(apiBlogs.value);
      }

      if (apiContributors.status === 'fulfilled' && Array.isArray(apiContributors.value) && apiContributors.value.length > 0) {
        setContributors(apiContributors.value);
      }
      return true;
    } catch (e) {
      console.warn('Could not refresh from DB:', e);
      return false;
    }
  }, [formatApiProduct]);

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const [apiProducts, apiOrders, apiVideos, apiBlogs, apiContributors] = await Promise.allSettled([
          fetchProductsApi(),
          fetchOrdersApi(),
          fetchVideosApi(),
          fetchBlogsApi(),
          fetchContributorsApi()
        ]);

        if (apiProducts.status === 'fulfilled' && Array.isArray(apiProducts.value) && apiProducts.value.length > 0) {
          const formatted = apiProducts.value.map(formatApiProduct);
          setProducts(formatted);
        }
        if (apiOrders.status === 'fulfilled' && Array.isArray(apiOrders.value)) {
          setOrders(apiOrders.value);
        }
        if (apiVideos.status === 'fulfilled' && Array.isArray(apiVideos.value) && apiVideos.value.length > 0) {
          setVideos(apiVideos.value);
        }
        if (apiBlogs.status === 'fulfilled' && Array.isArray(apiBlogs.value) && apiBlogs.value.length > 0) {
          setBlogs(apiBlogs.value);
        }
        if (apiContributors.status === 'fulfilled' && Array.isArray(apiContributors.value) && apiContributors.value.length > 0) {
          setContributors(apiContributors.value);
        }
      } catch (error) {
        console.warn('Could not load data from API:', error.message);
      } finally {
        setProductsLoadedFromApi(true);
      }
    };

    loadInitialData();
  }, [formatApiProduct]);

  // Real-time synchronization across all tabs and background auto-sync
  useEffect(() => {
    let channel = null;

    const handleSyncEvent = (payload) => {
      if (!payload || !payload.type) return;
      if (payload.type === 'PRODUCT_SAVED') {
        const newProd = payload.product;
        if (newProd && newProd.id) {
          setProducts(prev => {
            const cleanId = String(newProd.id).replace(/^db-/, '').replace(/^temp-/, '');
            const filtered = prev.filter(p => {
              const pid = String(p.id).replace(/^db-/, '').replace(/^temp-/, '');
              return pid !== cleanId;
            });
            return [newProd, ...filtered];
          });
        }
      } else if (payload.type === 'PRODUCT_DELETED') {
        const delId = String(payload.id).replace(/^db-/, '');
        setProducts(prev => prev.filter(p => String(p.id).replace(/^db-/, '') !== delId));
      }
    };

    try {
      if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
        channel = new BroadcastChannel('pallywear_realtime_sync');
        channel.onmessage = (event) => handleSyncEvent(event.data);
      }
    } catch (e) {}

    const handleStorageEvent = (e) => {
      if (e.key === 'pallywear_sync_event' && e.newValue) {
        try {
          const payload = JSON.parse(e.newValue);
          handleSyncEvent(payload);
        } catch (err) {}
      }
    };
    window.addEventListener('storage', handleStorageEvent);

    // Periodic silent refresh every 10 seconds for active visitors (0ms lag when admin adds products)
    const syncInterval = setInterval(() => {
      if (typeof document !== 'undefined' && !document.hidden) {
        fetchProductsApi()
          .then(data => {
            if (Array.isArray(data) && data.length > 0) {
              const formatted = data.map(formatApiProduct);
              setProducts(formatted);
            }
          })
          .catch(() => {});
      }
    }, 10000);

    const handleFocusSync = () => {
      fetchProductsApi()
        .then(data => {
          if (Array.isArray(data) && data.length > 0) {
            const formatted = data.map(formatApiProduct);
            setProducts(formatted);
          }
        })
        .catch(() => {});
    };

    window.addEventListener('focus', handleFocusSync);
    window.addEventListener('visibilitychange', handleFocusSync);

    return () => {
      if (channel) channel.close();
      window.removeEventListener('storage', handleStorageEvent);
      clearInterval(syncInterval);
      window.removeEventListener('focus', handleFocusSync);
      window.removeEventListener('visibilitychange', handleFocusSync);
    };
  }, [formatApiProduct]);

  // Sync products to local storage
  useEffect(() => {
    const safeProducts = sanitizeProductsForStorage(products);
    try {
      localStorage.setItem('pallywear_products', JSON.stringify(safeProducts));
      localStorage.setItem('pallywear_catalog_version', CATALOG_VERSION);
    } catch (error) {
      console.warn('Unable to persist products to localStorage:', error);
      localStorage.removeItem('pallywear_products');
      localStorage.removeItem('pallywear_catalog_version');
    }
  }, [products]);
  const [cart, setCart] = useState(() => {
    try {
      const saved = localStorage.getItem('pallywear_cart');
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  });
  const [wishlist, setWishlist] = useState(() => {
    try {
      const saved = localStorage.getItem('pallywear_wishlist');
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  });
  const [savedAddresses, setSavedAddresses] = useState(() => {
    try {
      const saved = localStorage.getItem('pallywear_addresses');
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  });
  const [promoCode, setPromoCode] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null); // { code: 'LUXE20', discount: 0.20 }
  const [orders, setOrders] = useState(() => {
    try {
      const saved = localStorage.getItem('pallywear_orders');
      return saved ? JSON.parse(saved) : DEFAULT_ORDERS;
    } catch (e) {
      return DEFAULT_ORDERS;
    }
  });

  // Sync user state to local storage
  useEffect(() => {
    if (user) {
      localStorage.setItem('pallywear_user', JSON.stringify(user));
    } else {
      localStorage.removeItem('pallywear_user');
    }
  }, [user]);

  // Auth operations
  const loginUser = async (email, password) => {
    const cleanEmail = email.trim().toLowerCase();

    if (cleanEmail === 'pallywear@gmail.com' && password === 'pally@123') {
      const adminUser = { email: cleanEmail, fullName: 'Administrator', role: 'admin', phone: '', cart: [], wishlist: [], addresses: [] };
      setUser(adminUser);
      setCart([]);
      setWishlist([]);
      setSavedAddresses([]);
      setAuthModalOpen(false);
      showToast('Logged in as Admin!', 'success');
      return true;
    }

    try {
      const data = await loginUserApi({ email: cleanEmail, password });
      const loggedUser = {
        email: cleanEmail,
        fullName: data.user?.fullName || 'Customer',
        role: data.user?.role || 'user',
        phone: data.user?.phone || ''
      };
      setUser(loggedUser);

      // Merge guest cart with database cart
      const dbCart = data.user?.cart || [];
      const mergedCart = [...dbCart];
      cart.forEach(localItem => {
        const exists = mergedCart.some(dbItem =>
          dbItem.product?.id === localItem.product?.id &&
          dbItem.size === localItem.size &&
          dbItem.color === localItem.color
        );
        if (!exists) {
          mergedCart.push(localItem);
        }
      });
      setCart(mergedCart);

      // Merge guest wishlist with database wishlist
      const dbWishlist = data.user?.wishlist || [];
      const mergedWishlist = Array.from(new Set([...dbWishlist, ...wishlist]));
      setWishlist(mergedWishlist);

      // Load database saved addresses
      setSavedAddresses(data.user?.addresses || []);

      setAuthModalOpen(false);
      showToast(`Welcome back, ${loggedUser.fullName}!`, 'success');
      return true;
    } catch (error) {
      showToast(error.message || 'Login failed', 'error');
      return false;
    }
  };

  const registerUser = async (email, fullName, phone, password) => {
    const cleanEmail = email.trim().toLowerCase();
    const cleanPhone = phone.trim();

    try {
      const data = await registerUserApi({ email: cleanEmail, fullName, phone: cleanPhone, password });
      const newUser = {
        email: cleanEmail,
        phone: cleanPhone,
        fullName,
        role: data.user?.role || 'user'
      };
      setUser(newUser);
      
      // Keep guest cart & wishlist synced to new account
      setCart(cart);
      setWishlist(wishlist);
      setSavedAddresses([]);

      setAuthModalOpen(false);
      showToast(data.message || 'Welcome to PallyWear!', 'success');
      return true;
    } catch (error) {
      showToast(error.message || 'Registration failed', 'error');
      return false;
    }
  };

  const logoutUser = () => {
    setUser(null);
    setCart([]);
    setWishlist([]);
    setSavedAddresses([]);
    setOrders([]);
    showToast('Logged out successfully.');
  };

  // Filter & Search states — initialized with URL deep link if present
  const [filters, setFilters] = useState(() => {
    const urlFilters = parseShopFiltersFromLocation();
    return {
      search: '',
      imageSearchPreview: null,
      imageSearchName: '',
      category: (urlFilters && urlFilters.category) || 'All',
      gender: (urlFilters && urlFilters.gender) || 'All',
      subCategory: (urlFilters && urlFilters.subCategory) || 'All',
      size: 'All',
      color: 'All',
      rating: 'All',
      priceRange: 10000, // Max price slider for INR pricing
      sortBy: 'featured' // featured, price-low, price-high, rating
    };
  });

  // Toast Notification state
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  // Info Modal state (Help & About footer links)
  const [infoModalState, setInfoModalState] = useState({ open: false, key: 'faq' });

  const openInfoModal = (key = 'faq') => {
    setInfoModalState({ open: true, key });
  };

  const closeInfoModal = () => {
    setInfoModalState({ open: false, key: 'faq' });
  };

  const setInfoModalKey = (key) => {
    setInfoModalState({ open: true, key });
  };

  // Laundry animation state
  const [laundryLandedCount, setLaundryLandedCount] = useState(0);

  // Apply dark mode theme to body element
  useEffect(() => {
    const root = window.document.documentElement;
    if (darkMode) {
      root.setAttribute('data-theme', 'dark');
    } else {
      root.removeAttribute('data-theme');
    }
    localStorage.setItem('pallywear_dark', JSON.stringify(darkMode));
  }, [darkMode]);

  // Sync cart to local storage
  useEffect(() => {
    localStorage.setItem('pallywear_cart', JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    localStorage.setItem('pallywear_wishlist', JSON.stringify(wishlist));
  }, [wishlist]);

  useEffect(() => {
    localStorage.setItem('pallywear_addresses', JSON.stringify(savedAddresses));
  }, [savedAddresses]);

  // Sync orders to local storage
  useEffect(() => {
    localStorage.setItem('pallywear_orders', JSON.stringify(orders));
  }, [orders]);

  // Sync logged-in user cart, wishlist, and addresses to backend database
  useEffect(() => {
    if (user && user.role !== 'admin') {
      const syncData = async () => {
        try {
          await syncUserDataApi({
            email: user.email,
            cart,
            wishlist,
            addresses: savedAddresses
          });
        } catch (error) {
          console.warn('Could not sync user data to database:', error.message);
        }
      };
      syncData();
    }
  }, [cart, wishlist, savedAddresses, user]);

  // Fetch user orders or all orders (if admin) from database when user logs in/changes
  useEffect(() => {
    const loadOrders = async () => {
      try {
        if (user && user.role === 'admin') {
          // Admin gets all platform orders
          const allOrders = await fetchOrdersApi(null);
          if (Array.isArray(allOrders)) {
            setOrders(allOrders);
          }
        } else if (user && user.email) {
          // Regular user gets their orders
          const userOrders = await fetchOrdersApi(user.email);
          if (Array.isArray(userOrders)) {
            setOrders(userOrders);
          }
        } else {
          // Guest user: preserve local storage orders
          const saved = localStorage.getItem('pallywear_orders');
          if (saved) {
            try {
              setOrders(JSON.parse(saved));
            } catch (e) {}
          }
        }
      } catch (error) {
        console.warn('Could not fetch orders:', error.message);
      }
    };
    loadOrders();
  }, [user]);

  const toggleDarkMode = () => setDarkMode(!darkMode);

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast(prev => ({ ...prev, show: false }));
    }, 3000);
  };

  // Custom Router Navigation helper with bidirectional browser address bar sync
  const setView = useCallback((view, item = null, pushHistory = true) => {
    setActiveView(view);
    if (view === 'product-detail' && item) {
      setActiveProduct(item);
    } else if (view === 'blog-post' && item) {
      setActiveBlogPost(item);
    } else if (item && !['product-detail', 'blog-post'].includes(view)) {
      setActiveProduct(item);
    }

    if (typeof window !== 'undefined' && pushHistory) {
      let targetPath = '/';
      let pageTitle = 'PallyWear | Luxury Streetwear & Apparel';

      switch (view) {
        case 'shop':
          targetPath = '/shop';
          pageTitle = 'PallyWear | Shop Luxury Apparel Collections';
          break;
        case 'sitemap':
          targetPath = '/sitemap';
          pageTitle = 'PallyWear | Visual Site Map & Active Directory';
          break;
        case 'about':
          targetPath = '/about';
          pageTitle = 'PallyWear | About Us & Brand Heritage';
          break;
        case 'blog':
          targetPath = '/blog';
          pageTitle = 'PallyWear | Editorial Journal & Streetwear Insights';
          break;
        case 'blog-post':
          targetPath = item ? `/blog-post?id=${item.slug || item.id}` : '/blog-post';
          pageTitle = item ? `PallyWear Journal | ${item.title}` : 'PallyWear | Journal Post';
          break;
        case 'account':
          targetPath = '/account';
          pageTitle = 'PallyWear | Customer Account & Order History';
          break;
        case 'track-order':
          targetPath = '/track-order';
          pageTitle = 'PallyWear | Track Order Status Live';
          break;
        case 'checkout':
          targetPath = '/checkout';
          pageTitle = 'PallyWear | Secure Checkout & Payment';
          break;
        case 'admin':
          targetPath = '/admin';
          pageTitle = 'PallyWear | Admin Management Console';
          break;
        case 'product-detail':
          targetPath = item ? `/product-detail?id=${item.id}` : '/product-detail';
          pageTitle = item ? `PallyWear | ${item.name}` : 'PallyWear | Product Details';
          break;
        case 'home':
        default:
          targetPath = '/';
          pageTitle = 'Custom Apparel & Promotional Products | Pallywear';
          break;
      }

      document.title = pageTitle;

      try {
        const currentFull = window.location.pathname + (window.location.search || '') + (window.location.hash || '');
        if (currentFull !== targetPath) {
          window.history.pushState({ view, itemId: item?.id }, pageTitle, targetPath);
        }
      } catch (e) {
        // Fallback
      }
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Synchronize browser address bar navigation (Back / Forward buttons & direct URL access)
  useEffect(() => {
    const handleUrlChange = () => {
      const detectedView = getViewFromLocation();
      setActiveView(detectedView);

      // Clean up legacy # or #home in the URL bar
      if (typeof window !== 'undefined' && window.location.hash) {
        const rawHash = window.location.hash.replace(/^#\/?/, '').toLowerCase().split('?')[0];
        if (rawHash === 'home' || rawHash === '') {
          const cleanUrl = (window.location.pathname || '/') + (window.location.search || '');
          window.history.replaceState({ view: detectedView }, document.title, cleanUrl || '/');
        }
      }

      if (detectedView === 'shop') {
        const urlFilters = parseShopFiltersFromLocation();
        if (urlFilters) {
          setFilters(prev => ({
            ...prev,
            gender: urlFilters.gender,
            subCategory: urlFilters.subCategory,
            category: urlFilters.category,
            search: '',
            imageSearchPreview: null
          }));
        }
      }
    };

    // Run once on mount to clear any unwanted # from the URL bar
    handleUrlChange();

    window.addEventListener('popstate', handleUrlChange);
    window.addEventListener('hashchange', handleUrlChange);

    return () => {
      window.removeEventListener('popstate', handleUrlChange);
      window.removeEventListener('hashchange', handleUrlChange);
    };
  }, [getViewFromLocation]);

  // Synchronize document title and SEO meta descriptions for Google / Social crawlers
  useEffect(() => {
    let title = 'Custom Apparel & Promotional Products | Pallywear';
    let description = 'Order premium custom apparel, sports jerseys, and corporate wear from Pallywear. High-quality promotional products with MOQ starting from 1 piece.';

    if (activeView === 'about') {
      title = 'About Pallywear | Custom Apparel & Promotional Wear';
      description = 'Discover Pallywear, your trusted custom apparel manufacturer. We offer premium sports jerseys, corporate wear, and promotional apparel with low 1-piece MOQ.';
    } else if (activeView === 'shop') {
      const sub = (filters.subCategory || '').toLowerCase();
      const cat = (filters.category || '').toLowerCase();
      const gender = (filters.gender || '').toLowerCase();

      if (gender.includes('sport') || sub.includes('sport') || cat.includes('sport') || sub.includes('jersey')) {
        title = 'Custom Sports T-Shirts & Jerseys | Pallywear';
        description = 'Design custom sports t-shirts and breathable jerseys with Pallywear. High-performance activewear, vibrant sublimation printing, and low MOQ starting at 1 piece.';
      } else if (gender.includes('women') && (sub.includes('polo') || cat.includes('polo') || sub === 'polos')) {
        title = "Women's Polo T-Shirts & Custom Collar Tees | Pallywear";
        description = "Shop premium women's polo t-shirts at Pallywear. High-quality custom collar tees with logo embroidery or printing. Tailored fits with low MOQ from 1 piece.";
      } else if ((gender.includes('men') || gender === 'all') && (sub.includes('polo') || cat.includes('polo') || sub === 'polos')) {
        title = "Men's Polo T-Shirts & Custom Collar Tees | Pallywear";
        description = "Shop premium men's polo t-shirts at Pallywear. Discover high-grade custom & classic polo tees with custom embroidery or logo printing. Order from 1 piece.";
      } else {
        title = 'Custom Apparel & Promotional Products | Pallywear';
        description = 'Order premium custom apparel, sports jerseys, and corporate wear from Pallywear. High-quality promotional products with MOQ starting from 1 piece.';
      }
    } else if (activeView === 'product-detail' && activeProduct) {
      title = `${activeProduct.name} | Pallywear`;
      description = activeProduct.description || description;
    } else if (activeView === 'blog' || activeView === 'blog-post') {
      title = activeBlogPost ? `${activeBlogPost.title} | Pallywear Journal` : 'Pallywear Journal | Custom Apparel Insights';
      description = activeBlogPost?.excerpt || 'Discover the latest articles and custom apparel guides from Pallywear.';
    }

    if (typeof document !== 'undefined') {
      document.title = title;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) metaDesc.setAttribute('content', description);
      const ogDesc = document.querySelector('meta[property="og:description"]');
      if (ogDesc) ogDesc.setAttribute('content', description);
      const ogTitle = document.querySelector('meta[property="og:title"]');
      if (ogTitle) ogTitle.setAttribute('content', title);
    }
  }, [activeView, filters.category, filters.subCategory, filters.gender, activeProduct, activeBlogPost]);

  // Cart operations
  const addToCart = (product, size, color, quantity = 1, customization = null, customPrice = null) => {
    if (!size || !color) {
      showToast('Please select a size and color.', 'error');
      return;
    }
    
    // Check single category constraint
    if (cart.length > 0) {
      const activeCategory = cart[0].product.category;
      if (product.category !== activeCategory) {
        showToast(`You can only order items from a single category at a time. Your cart contains '${activeCategory}' items.`, 'error');
        return;
      }
    }

    const sizeSurcharge = getSizeExtraAmount(size);
    const finalUnitPrice = (customPrice !== null && customPrice !== undefined) ? customPrice : (product.price + sizeSurcharge);
    
    setCart(prevCart => {
      const existingItemIndex = prevCart.findIndex(
        item => item.product.id === product.id && 
                item.size === size && 
                item.color === color &&
                JSON.stringify(item.customization || null) === JSON.stringify(customization || null)
      );

      if (existingItemIndex > -1) {
        const newCart = [...prevCart];
        newCart[existingItemIndex].quantity += quantity;
        showToast(`Updated ${product.name} quantity in Cart!`);
        return newCart;
      } else {
        showToast(`Added ${product.name} to Cart!`);
        return [...prevCart, { 
          product, 
          size, 
          color, 
          quantity,
          unitPrice: finalUnitPrice,
          price: finalUnitPrice,
          customization: customization || null
        }];
      }
    });
  };

  const removeFromCart = (productId, size, color, customization = null) => {
    setCart(prevCart => prevCart.filter(
      item => !(
        item.product.id === productId && 
        item.size === size && 
        item.color === color &&
        (!customization || JSON.stringify(item.customization || null) === JSON.stringify(customization || null))
      )
    ));
    showToast('Item removed from cart.');
  };

  const updateCartQuantity = (productId, size, color, quantity, customization = null) => {
    if (quantity <= 0) {
      removeFromCart(productId, size, color, customization);
      return;
    }
    setCart(prevCart => prevCart.map(
      item => (
        item.product.id === productId && 
        item.size === size && 
        item.color === color &&
        (!customization || JSON.stringify(item.customization || null) === JSON.stringify(customization || null))
      )
        ? { ...item, quantity }
        : item
    ));
  };

  const clearCart = () => {
    setCart([]);
  };

  // Promo Code handling
  const applyPromoCode = (code) => {
    const sanitized = code.toUpperCase().trim();
    if (sanitized === 'LUXE20') {
      setAppliedPromo({ code: 'LUXE20', discount: 0.20 });
      showToast('Promo code LUXE20 applied: 20% discount!', 'success');
      return true;
    } else if (sanitized === 'FREE5') {
      setAppliedPromo({ code: 'FREE5', discount: 0.05 });
      showToast('Promo code FREE5 applied: 5% discount!', 'success');
      return true;
    } else {
      showToast('Invalid promo code.', 'error');
      return false;
    }
  };

  const removePromoCode = () => {
    setAppliedPromo(null);
    showToast('Promo code removed.');
  };

  const toggleWishlist = (productId) => {
    setWishlist(prev => {
      if (prev.includes(productId)) {
        showToast('Removed item from your wishlist.');
        return prev.filter(id => id !== productId);
      }
      showToast('Added item to your wishlist.');
      return [...prev, productId];
    });
  };

  const removeFromWishlist = (productId) => {
    setWishlist(prev => prev.filter(id => id !== productId));
    showToast('Removed item from your wishlist.');
  };

  const addAddress = (address) => {
    setSavedAddresses(prev => [...prev, address]);
    showToast('Saved new shipping address.');
  };

  const removeAddress = (index) => {
    setSavedAddresses(prev => prev.filter((_, i) => i !== index));
    showToast('Removed saved address.');
  };

  // Order placing
  const placeOrder = (shippingInfo, paymentInfo) => {
    const subtotal = cart.reduce((sum, item) => {
      const uPrice = item.unitPrice || item.price || (item.product.price + getSizeExtraAmount(item.size));
      return sum + uPrice * item.quantity;
    }, 0);
    const discount = appliedPromo ? subtotal * appliedPromo.discount : 0;
    const shipping = subtotal > 100 || subtotal === 0 ? 0 : 9.99;
    const total = subtotal - discount + shipping;

    const newOrder = {
      orderId: 'ORD-' + Math.floor(100000 + Math.random() * 900000),
      date: new Date().toLocaleDateString() + ' ' + new Date().toLocaleTimeString(),
      items: [...cart],
      subtotal,
      discount,
      shipping,
      total,
      shippingInfo,
      paymentInfo,
      status: 'Processing'
    };

    // Deduct stock levels
    setProducts(prevProducts => prevProducts.map(p => {
      const cartItems = cart.filter(item => item.product.id === p.id);
      const totalBought = cartItems.reduce((sum, item) => sum + item.quantity, 0);
      return {
        ...p,
        stock: Math.max(0, p.stock - totalBought)
      };
    }));

    // Post order to database
    const userEmail = user ? user.email : shippingInfo.email || 'guest@pallywear.com';
    const saveOrder = async () => {
      try {
        await createOrderApi({
          orderId: newOrder.orderId,
          userEmail,
          items: newOrder.items,
          subtotal,
          discount,
          shipping,
          total,
          shippingInfo,
          paymentInfo,
          status: newOrder.status
        });
      } catch (error) {
        console.warn('Could not save order to database:', error.message);
      }
    };
    saveOrder();

    setOrders(prevOrders => [newOrder, ...prevOrders]);
    clearCart();
    setAppliedPromo(null);
    showToast('Order placed successfully!', 'success');
    return newOrder;
  };

  // Product management for Admin panel with Direct Database Synchronization
  const addProduct = async (newProduct) => {
    let uploadedImages = [];
    if (Array.isArray(newProduct.images) && newProduct.images.length > 0) {
      const uploadResults = await Promise.all(newProduct.images.map(async (image, index) => {
        let currentImageVal = typeof image === 'object' ? (image.preview || image.url || image.filePath || '') : image;
        if (typeof currentImageVal === 'string' && currentImageVal.length > 0) {

          // Handle blob: URLs — fetch the binary data and upload to server as a permanent file
          if (currentImageVal.startsWith('blob:')) {
            try {
              const blobResponse = await fetch(currentImageVal);
              const blobData = await blobResponse.blob();
              const ext = blobData.type.split('/')[1] || 'jpg';
              const file = new File([blobData], `product-${Date.now()}-${index}.${ext}`, { type: blobData.type });
              const res = await uploadImageApi(file, { productName: newProduct.name, index });
              if (res && (res.url || res.filePath)) {
                return { url: normalizeUploadPath(res.url || res.filePath) };
              }
            } catch (e) {
              console.warn('Blob upload failed, converting to permanent Base64 Data URL to prevent image loss:', e.message);
              try {
                const blobResponse = await fetch(currentImageVal);
                const blobData = await blobResponse.blob();
                const base64Str = await new Promise((resolve) => {
                  const reader = new FileReader();
                  reader.onloadend = () => resolve(reader.result);
                  reader.onerror = () => resolve(null);
                  reader.readAsDataURL(blobData);
                });
                if (base64Str) {
                  return { url: base64Str };
                }
              } catch (convErr) {}
            }
          }

          // Handle data:image/ URLs — convert to File and upload to server
          if (currentImageVal.startsWith('data:image/')) {
            try {
              const parts = currentImageVal.split(',');
              const mime = parts[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
              const bstr = atob(parts[1]);
              let n = bstr.length;
              const u8arr = new Uint8Array(n);
              while (n--) {
                u8arr[n] = bstr.charCodeAt(n);
              }
              const ext = mime.split('/')[1] || 'jpg';
              const file = new File([u8arr], `product-${Date.now()}-${index}.${ext}`, { type: mime });
              const res = await uploadImageApi(file, { productName: newProduct.name, index });
              if (res && (res.url || res.filePath)) {
                return { url: normalizeUploadPath(res.url || res.filePath) };
              }
            } catch (e) {
              console.warn('Data URL upload note, preserving direct image data:', e.message);
              return { url: currentImageVal };
            }
          }

          let clean = currentImageVal;
          if (API_BASE_URL && clean.startsWith(API_BASE_URL)) {
            clean = clean.substring(API_BASE_URL.length);
          }
          return { url: normalizeUploadPath(clean) || clean };
        }
        const file = image?.file instanceof File ? image.file : null;
        if (!file) {
          return { url: currentImageVal || '' };
        }
        try {
          const res = await uploadImageApi(file, { productName: newProduct.name, index });
          return { url: normalizeUploadPath(res.url || res.filePath) };
        } catch (uploadErr) {
          console.warn('Image upload note:', uploadErr.message);
          return { url: currentImageVal || image?.preview || '' };
        }
      }));
      uploadedImages = uploadResults.map(r => r.url || r.filePath || r.imageUrl || '').filter(Boolean);
    }

    if (uploadedImages.length === 0) {
      const mainImg = typeof newProduct.image === 'string' ? newProduct.image : (newProduct.image?.preview || newProduct.imageUrl);
      uploadedImages = [mainImg || '/images/cat-slide-men-polo.JPG'];
    }

    const finalMainImage = uploadedImages[0];

    let targetCat = newProduct.category || 'Polos';
    let targetGender = newProduct.gender || 'Mens';
    const pName = (newProduct.name || '').toLowerCase();
    if (targetCat === 'Polos' && (pName.includes('jersey') || targetGender === 'Sports')) {
      targetCat = 'Sports Jerseys';
      targetGender = 'Sports';
    } else if (targetCat === 'Sports Jerseys' || targetCat === 'Sports T-Shirts') {
      targetGender = 'Sports';
    }

    const tempId = `temp-${Date.now()}`;
    const productToAdd = {
      ...newProduct,
      category: targetCat,
      gender: targetGender,
      id: tempId,
      price: parseFloat(newProduct.price) || 0,
      stock: parseInt(newProduct.stock, 10) || 10,
      description: newProduct.description || '',
      imageUrl: finalMainImage,
      image: getProductImage(finalMainImage, targetCat),
      images: uploadedImages.map(img => getProductImage(img, targetCat)),
      specifications: newProduct.specifications || [],
      rating: 5.0,
      reviews: 0
    };

    // 1. Instantly prepend product so it renders at 0ms in search, lists, and admin!
    setProducts(prev => [productToAdd, ...prev.filter(p => String(p.id) !== tempId)]);

    // 2. Instantly preload product image in browser memory for 0ms paint
    try {
      if (typeof window !== 'undefined' && productToAdd.image) {
        const preloader = new Image();
        preloader.src = productToAdd.image;
      }
      if (typeof window !== 'undefined') {
        if ('BroadcastChannel' in window) {
          const ch = new BroadcastChannel('pallywear_realtime_sync');
          ch.postMessage({ type: 'PRODUCT_SAVED', product: productToAdd });
          ch.close();
        }
        localStorage.setItem('pallywear_sync_event', JSON.stringify({ type: 'PRODUCT_SAVED', product: productToAdd, time: Date.now() }));
      }
    } catch (e) {}

    // 3. Persist to backend database
    let savedProduct = null;
    try {
      savedProduct = await createProductApi({
        name: newProduct.name,
        price: parseFloat(newProduct.price) || 0,
        category: targetCat,
        gender: targetGender,
        stock: parseInt(newProduct.stock, 10) || 10,
        description: newProduct.description || '',
        imageUrl: finalMainImage,
        tag: newProduct.tag || 'New',
        sizes: newProduct.sizes || ['S', 'M', 'L', 'XL'],
        colors: newProduct.colors || ['Black', 'White'],
        images: uploadedImages,
        specifications: newProduct.specifications || []
      });
    } catch (apiErr) {
      console.warn('DB API note on addProduct (falling back to local storage):', apiErr.message);
    }

    if (savedProduct?.id) {
      const realDbId = `db-${savedProduct.id}`;
      const finalized = { ...productToAdd, id: realDbId };
      setProducts(prev => prev.map(p => (String(p.id) === tempId ? finalized : p)));
      try {
        if (typeof window !== 'undefined') {
          if ('BroadcastChannel' in window) {
            const ch = new BroadcastChannel('pallywear_realtime_sync');
            ch.postMessage({ type: 'PRODUCT_SAVED', product: finalized });
            ch.close();
          }
          localStorage.setItem('pallywear_sync_event', JSON.stringify({ type: 'PRODUCT_SAVED', product: finalized, time: Date.now() }));
        }
      } catch (e) {}
      showToast('✓ Product saved with crystal-clear uploaded images!', 'success');
      return finalized;
    }

    showToast('✓ Product saved with crystal-clear uploaded images!', 'success');
    return productToAdd;
  };

  const deleteProduct = async (id) => {
    const cleanId = String(id).replace(/^db-/, '');
    try {
      await deleteProductApi(cleanId);
      setProducts(prev => prev.filter(p => String(p.id).replace(/^db-/, '') !== cleanId));
      try {
        if (typeof window !== 'undefined') {
          if ('BroadcastChannel' in window) {
            const ch = new BroadcastChannel('pallywear_realtime_sync');
            ch.postMessage({ type: 'PRODUCT_DELETED', id: cleanId });
            ch.close();
          }
          localStorage.setItem('pallywear_sync_event', JSON.stringify({ type: 'PRODUCT_DELETED', id: cleanId, time: Date.now() }));
        }
      } catch (e) {}
      showToast('✓ Product permanently deleted from Database.', 'success');
    } catch (error) {
      console.error('Error deleting product from DB:', error);
      setProducts(prev => prev.filter(p => String(p.id).replace(/^db-/, '') !== cleanId));
      if (error.message && (error.message.includes('not found') || error.message.includes('404'))) {
        showToast('✓ Product removed from catalog.', 'info');
      } else {
        showToast('Product removed: ' + (error.message || 'Database sync'), 'warning');
      }
    }
  };

  const updateProduct = async (updatedProduct) => {
    let uploadedImages = [];
    if (Array.isArray(updatedProduct.images) && updatedProduct.images.length > 0) {
      const uploadResults = await Promise.all(updatedProduct.images.map(async (image, index) => {
        let currentImageVal = typeof image === 'object' ? (image.preview || image.url || image.filePath || '') : image;
        if (typeof currentImageVal === 'string' && currentImageVal.trim().startsWith('{')) {
          try {
            const parsed = JSON.parse(currentImageVal);
            currentImageVal = parsed.preview || parsed.url || parsed.filePath || currentImageVal;
          } catch (e) {}
        }
        if (typeof currentImageVal === 'string' && currentImageVal.length > 0) {
          // Handle blob: URLs — fetch binary and upload to server as permanent file
          if (currentImageVal.startsWith('blob:')) {
            try {
              const blobResponse = await fetch(currentImageVal);
              const blobData = await blobResponse.blob();
              const ext = blobData.type.split('/')[1] || 'jpg';
              const file = new File([blobData], `product-${Date.now()}-${index}.${ext}`, { type: blobData.type });
              const res = await uploadImageApi(file, { productName: updatedProduct.name, index });
              if (res && (res.url || res.filePath)) {
                return { url: normalizeUploadPath(res.url || res.filePath) };
              }
            } catch (e) {
              console.warn('Blob upload failed in updateProduct, converting to permanent Base64 Data URL:', e.message);
              try {
                const blobResponse = await fetch(currentImageVal);
                const blobData = await blobResponse.blob();
                const base64Str = await new Promise((resolve) => {
                  const reader = new FileReader();
                  reader.onloadend = () => resolve(reader.result);
                  reader.onerror = () => resolve(null);
                  reader.readAsDataURL(blobData);
                });
                if (base64Str) {
                  return { url: base64Str };
                }
              } catch (convErr) {}
            }
          }

          if (currentImageVal.startsWith('data:image/')) {
            try {
              const parts = currentImageVal.split(',');
              const mime = parts[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
              const bstr = atob(parts[1]);
              let n = bstr.length;
              const u8arr = new Uint8Array(n);
              while (n--) {
                u8arr[n] = bstr.charCodeAt(n);
              }
              const ext = mime.split('/')[1] || 'jpg';
              const file = new File([u8arr], `product-${Date.now()}-${index}.${ext}`, { type: mime });
              const res = await uploadImageApi(file, { productName: updatedProduct.name, index });
              if (res && (res.url || res.filePath)) {
                return { url: normalizeUploadPath(res.url || res.filePath) };
              }
            } catch (e) {
              console.warn('Server upload note in updateProduct:', e.message);
              return { url: currentImageVal };
            }
          }

          let clean = currentImageVal;
          if (API_BASE_URL && clean.startsWith(API_BASE_URL)) {
            clean = clean.substring(API_BASE_URL.length);
          }
          return { url: normalizeUploadPath(clean) || clean };
        }
        const file = image?.file instanceof File ? image.file : null;
        if (!file) {
          return { url: currentImageVal || '' };
        }
        try {
          const res = await uploadImageApi(file, { productName: updatedProduct.name, index });
          return { url: normalizeUploadPath(res.url || res.filePath) };
        } catch (e) {
          return { url: currentImageVal || image?.preview || '' };
        }
      }));
      uploadedImages = uploadResults.map(r => r.url || r.filePath || r.imageUrl || '').filter(Boolean);
    }

    if (uploadedImages.length === 0) {
      const mainImg = typeof updatedProduct.image === 'string' ? updatedProduct.image : (updatedProduct.image?.preview || updatedProduct.imageUrl);
      uploadedImages = [mainImg || '/images/cat-slide-men-polo.JPG'];
    }

    const finalMainImage = uploadedImages[0];
    const cleanId = String(updatedProduct.id);

    let targetCat = updatedProduct.category || 'Polos';
    let targetGender = updatedProduct.gender || 'Mens';
    const pName = (updatedProduct.name || '').toLowerCase();
    if (targetCat === 'Polos' && (pName.includes('jersey') || targetGender === 'Sports')) {
      targetCat = 'Sports Jerseys';
      targetGender = 'Sports';
    } else if (targetCat === 'Sports Jerseys' || targetCat === 'Sports T-Shirts') {
      targetGender = 'Sports';
    }

    try {
      await updateProductApi(cleanId, {
        name: updatedProduct.name,
        price: parseFloat(updatedProduct.price) || 0,
        category: targetCat,
        gender: targetGender,
        stock: parseInt(updatedProduct.stock, 10) || 10,
        description: updatedProduct.description || '',
        imageUrl: finalMainImage,
        tag: updatedProduct.tag || 'New',
        sizes: updatedProduct.sizes,
        colors: updatedProduct.colors,
        images: uploadedImages,
        specifications: updatedProduct.specifications || []
      });
    } catch (apiErr) {
      console.warn('DB API note on updateProduct:', apiErr.message);
    }

    const updatedObj = {
      ...updatedProduct,
      category: targetCat,
      gender: targetGender,
      price: parseFloat(updatedProduct.price) || 0,
      stock: parseInt(updatedProduct.stock, 10) || 10,
      description: updatedProduct.description || '',
      imageUrl: finalMainImage,
      image: getProductImage(finalMainImage, targetCat),
      images: uploadedImages.map(img => getProductImage(img, targetCat)),
      specifications: updatedProduct.specifications || []
    };

    setProducts(prev => prev.map(p => (String(p.id) === String(updatedProduct.id) ? updatedObj : p)));

    // Instantly preload updated image
    try {
      if (typeof window !== 'undefined' && updatedObj.image) {
        const preloader = new Image();
        preloader.src = updatedObj.image;
      }
      if (typeof window !== 'undefined') {
        if ('BroadcastChannel' in window) {
          const ch = new BroadcastChannel('pallywear_realtime_sync');
          ch.postMessage({ type: 'PRODUCT_SAVED', product: updatedObj });
          ch.close();
        }
        localStorage.setItem('pallywear_sync_event', JSON.stringify({ type: 'PRODUCT_SAVED', product: updatedObj, time: Date.now() }));
      }
    } catch (e) {}

    showToast('✓ Product updated with crystal-clear uploaded images!', 'success');
    return updatedObj;
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await updateOrderStatusApi(orderId, newStatus);
      setOrders(prevOrders => prevOrders.map(order => 
        order.orderId === orderId ? { ...order, status: newStatus } : order
      ));
      showToast(`Order status updated to ${newStatus}.`, 'success');
    } catch (error) {
      showToast(error.message || 'Could not update order status in database.', 'error');
    }
  };

  const deleteOrder = async (orderId) => {
    try {
      await deleteOrderApi(orderId);
      setOrders(prevOrders => prevOrders.filter(order => order.orderId !== orderId));
      showToast('Order deleted successfully.', 'success');
    } catch (error) {
      showToast(error.message || 'Could not delete order from database.', 'error');
    }
  };

  // Video Management for Showcase and Database persistence
  const [videos, setVideos] = useState(() => {
    try {
      const saved = localStorage.getItem('pallywear_videos');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed;
        }
      }
    } catch (e) {
      console.warn('Error reading stored videos:', e);
    }
    return DEFAULT_VIDEOS;
  });

  useEffect(() => {
    const loadVideos = async () => {
      try {
        const dbVideos = await fetchVideosApi();
        if (Array.isArray(dbVideos) && dbVideos.length > 0) {
          setVideos(dbVideos);
        }
      } catch (error) {
        console.warn('Could not fetch videos from DB API:', error.message);
      }
    };
    loadVideos();
  }, []);

  useEffect(() => {
    try {
      if (Array.isArray(videos) && videos.length > 0) {
        localStorage.setItem('pallywear_videos', JSON.stringify(videos));
      }
    } catch (e) {
      console.warn('Unable to persist videos to localStorage:', e);
    }
  }, [videos]);

  const addVideo = async (newVideo) => {
    const tempId = Date.now();
    const optimisticVid = { ...newVideo, id: tempId };
    setVideos(prev => [optimisticVid, ...prev.filter(v => v.src !== newVideo.src)]);
    showToast('Video added to showcase & database!', 'success');

    try {
      const res = await createVideoApi(newVideo);
      if (res && res.id) {
        setVideos(prev => prev.map(v => v.id === tempId ? { ...v, id: res.id } : v));
      }
    } catch (error) {
      console.warn('Video save note:', error.message);
    }
  };

  const updateVideo = async (id, updatedFields) => {
    const cleanId = String(id).replace(/^db-/, '');
    setVideos(prev => prev.map(v => String(v.id).replace(/^db-/, '') === cleanId ? { ...v, ...updatedFields } : v));
    showToast('Video updated successfully in database!', 'success');

    try {
      await updateVideoApi(cleanId, updatedFields);
    } catch (error) {
      console.warn('Video update note:', error.message);
    }
  };

  const deleteVideo = async (id) => {
    const cleanId = String(id).replace(/^db-/, '');
    setVideos(prev => prev.filter(v => String(v.id).replace(/^db-/, '') !== cleanId));
    showToast('Video deleted from database.');

    try {
      await deleteVideoApi(cleanId);
    } catch (error) {
      console.warn('Video delete note:', error.message);
    }
  };

  // Blog Management Methods
  const addBlog = async (newBlog) => {
    const tempId = Date.now();
    const optimisticBlog = {
      ...newBlog,
      id: tempId,
      publish_date: newBlog.publish_date || new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
    };

    if (newBlog.is_hero) {
      setBlogs(prev => [optimisticBlog, ...prev.map(b => ({ ...b, is_hero: 0 }))]);
    } else {
      setBlogs(prev => [optimisticBlog, ...prev]);
    }
    showToast('✓ Blog post added & saved to database!', 'success');

    try {
      const res = await createBlogApi(newBlog);
      if (res && res.id) {
        setBlogs(prev => prev.map(b => b.id === tempId ? { ...b, id: res.id } : b));
      }
    } catch (error) {
      console.warn('Blog creation note:', error.message);
    }
  };

  const updateBlog = async (id, updatedFields) => {
    const cleanId = String(id).replace(/^db-/, '');
    setBlogs(prev => prev.map(b => {
      const isTarget = String(b.id).replace(/^db-/, '') === cleanId || String(b.id) === String(id);
      if (isTarget) {
        return { ...b, ...updatedFields };
      }
      if (updatedFields.is_hero) {
        return { ...b, is_hero: 0 };
      }
      return b;
    }));
    showToast('✓ Blog post updated in database!', 'success');

    try {
      await updateBlogApi(cleanId, updatedFields);
    } catch (error) {
      console.warn('Blog update note:', error.message);
    }
  };

  const deleteBlog = async (id) => {
    const cleanId = String(id).replace(/^db-/, '');
    setBlogs(prev => prev.filter(b => String(b.id).replace(/^db-/, '') !== cleanId && String(b.id) !== String(id)));
    showToast('Blog post deleted from database.');

    try {
      await deleteBlogApi(cleanId);
    } catch (error) {
      console.warn('Blog delete note:', error.message);
    }
  };

  const setHeroBlog = async (id) => {
    await updateBlog(id, { is_hero: 1 });
  };

  const openBlogPost = (blog) => {
    setActiveBlogPost(blog);
    setView('blog-post', blog);
  };

  // Contributor Management Methods
  const addContributor = async (newContributor) => {
    const tempId = Date.now();
    const optimistic = { ...newContributor, id: tempId };
    setContributors(prev => [...prev, optimistic]);
    showToast('✓ Contributor profile added!', 'success');

    try {
      const res = await createContributorApi(newContributor);
      if (res && res.id) {
        setContributors(prev => prev.map(c => c.id === tempId ? { ...c, id: res.id } : c));
      }
    } catch (error) {
      console.warn('Contributor creation note:', error.message);
    }
  };

  const updateContributor = async (id, updatedFields) => {
    const cleanId = String(id).replace(/^db-/, '');
    setContributors(prev => prev.map(c => String(c.id).replace(/^db-/, '') === cleanId ? { ...c, ...updatedFields } : c));
    showToast('✓ Contributor updated in database!', 'success');

    try {
      await updateContributorApi(cleanId, updatedFields);
    } catch (error) {
      console.warn('Contributor update note:', error.message);
    }
  };

  const deleteContributor = async (id) => {
    const cleanId = String(id).replace(/^db-/, '');
    setContributors(prev => prev.filter(c => String(c.id).replace(/^db-/, '') !== cleanId));
    showToast('Contributor deleted from database.');

    try {
      await deleteContributorApi(cleanId);
    } catch (error) {
      console.warn('Contributor delete note:', error.message);
    }
  };

  return (
    <ShopContext.Provider value={{
      darkMode, toggleDarkMode,
      activeView, setView, activeProduct,
      activeBlogPost, setActiveBlogPost, openBlogPost,
      products, cart, addToCart, removeFromCart, updateCartQuantity, clearCart,
      wishlist, toggleWishlist, removeFromWishlist,
      savedAddresses, addAddress, removeAddress,
      promoCode, setPromoCode, appliedPromo, applyPromoCode, removePromoCode,
      orders, placeOrder, updateOrderStatus, deleteOrder,
      filters, setFilters,
      parseShopFiltersFromLocation, getSlugFromFilters,
      addProduct, deleteProduct, updateProduct,
      toast, showToast,
      infoModalState, openInfoModal, closeInfoModal, setInfoModalKey,
      laundryLandedCount, setLaundryLandedCount,
      videos, addVideo, updateVideo, deleteVideo,
      blogs, addBlog, updateBlog, deleteBlog, setHeroBlog,
      contributors, addContributor, updateContributor, deleteContributor,
      user, authModalOpen, setAuthModalOpen, loginUser, registerUser, logoutUser,
      refreshDbData
    }}>
      {children}
    </ShopContext.Provider>
  );
};
